import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, EventEmitter
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { FilterTypes } from '@shared/flight-itinerary/entities/filters';
import {
  CheckboxFilterVm, PickedRange, PickedRangeValues, RangePicketVm
} from '@shared/filters/entities/fetch-filters.response';
import { isEmpty } from '@app/util/util';
import { mapSorterLabel } from '@flight-results/mappers/filters.mapper';
import { AirFilterBarService } from '@shared/magic-filter-bar/air-filter-bar/services/air-filter-bar.service';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import { AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import { isCheckBoxByAirFilter, isCheckRangeByAirFilter } from '@app/mappers/global-filters/air-global-filters.mapper';
import { DIRECT_STOP_VALUE } from '@shared/magic-filter-bar/constants/filters.contants';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { FILTER_BAR_ICON } from '@flight-results/constants/filters.constants';

@Component({
  selector: 'app-air-filter-bar-slide',
  templateUrl: './air-filter-bar-slide.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AirFilterBarSlideComponent {

  @Input() barFilters: AirFilterVm[] = [];
  @Input() allFilter: AirFilterVm[] = [];
  @Input() orderCriteria = SorterTypes.Recommended;
  @Input() orderCriteriaSectorNumber: number;
  @Input() orderDesc: boolean;
  @Input() codePlaces: { departure: string, arrival: string };
  @Input() sectorsExpected: number;
  @Input() selectedSectors: number[];
  @Input() isBySector: boolean;
  @Input() isAirByPackage: boolean;
  @Input() sortFilterHasBeenChangedByPackage: boolean;

  @Input() pickedCheckboxes = [];
  @Input() pickedRanges = {} as PickedRange;
  @Input() pickedAirportCheckboxes = {} as AirportsFilter;

  @Output() sortEvent: EventEmitter<{
    filter: AirFilterVm
  }> = new EventEmitter<{
    filter: AirFilterVm
  }>();
  @Output() checkboxEvent: EventEmitter<{
    filter: AirFilterVm, isMultiple: boolean, checked: boolean,
  }> = new EventEmitter<{
    filter: AirFilterVm, isMultiple: boolean, checked: boolean,
  }>();
  @Output() rangeEvent: EventEmitter<{
    filter: AirFilterVm, isMultiple: boolean, checked: boolean,
  }> = new EventEmitter<{
    filter: AirFilterVm, isMultiple: boolean, checked: boolean,
  }>();

  public readonly icons = Icon;
  public readonly filterBarIcons = FILTER_BAR_ICON;

  constructor(
    private readonly translateService: TranslocoService,
    private readonly airFilterBarService: AirFilterBarService,
  ) { }

  get filterTypes(): typeof FilterTypes {
    return FilterTypes;
  }

  get sorterTabs(): AirFilterVm {
    return this.allFilter.find(item => item.type === FilterTypes.AirFilterTabs && !isEmpty(item.value));
  }

  get getSorterTitle(): string {
    return mapSorterLabel(this.orderCriteria, this.orderCriteriaSectorNumber, this.orderDesc);
  }

  get isActiveSorter(): boolean {
    return this.isAirByPackage ? !!this.sortFilterHasBeenChangedByPackage : !!this.orderCriteria;
  }

  private get isLastSector(): boolean {
    return this.isBySector && (this.selectedSectors?.length + 1) === this.sectorsExpected && this.sectorsExpected > 1;
  }

  public isCheckboxFilter(item: AirFilterVm): boolean {
    return isCheckBoxByAirFilter(item.type) && (item.value as CheckboxFilterVm[]).length > 0;
  }

  public isRangeFilter(item: AirFilterVm): boolean {
    return isCheckRangeByAirFilter(item.type);
  }

  public isMultipleSelection(filter: AirFilterVm): boolean {
    return this.mutableMutilpleFilter(filter) &&
      !this.isRangeFilter(filter) && Array.isArray(filter.value) && filter.value.length > 1;
  }

  public mutableMutilpleFilter(filter: AirFilterVm): boolean {
    if (filter.type === FilterTypes.Stops) {
      const selectedStops = (filter.value as CheckboxFilterVm[]).filter(value => this.pickedCheckboxes.includes(value.Text));
      const directHasBeenSelected = selectedStops.find(selected => selected.Text === DIRECT_STOP_VALUE);
      return !isEmpty(selectedStops) && (!directHasBeenSelected || (directHasBeenSelected && selectedStops.length > 1));
    } else {
      return true;
    }
  }

  public getFilterLabel(filter: AirFilterVm, range?: PickedRangeValues, key?: number): string {
    if (filter.type === FilterTypes.AirFilterTabs) {
      return this.getSortLabel(filter);
    } else if (this.isCheckboxFilter(filter)) {
      return this.getCheckboxLabel(filter, range, key);
    } else if (this.isRangeFilter(filter)) {
      return this.getRangeLabel(filter, range, key);
    } else {
      return this.translateService.translate(this.airFilterBarService.getItemLabel(filter.type));
    }
  }

  public isActiveCheckboxOption(values: CheckboxFilterVm[]): boolean {
    return !!values.find(value => this.pickedCheckboxes.includes(value.Text));
  }

  public showDirectFlightsCheckboxFilter(values: CheckboxFilterVm[]): boolean {
    const hasDirectFlights = values.some(option => option.Text === 0 && option.Count > 0);
    const hasSelectedNonDirectOption = this.pickedCheckboxes.some(selected => selected !== 0);
    return hasDirectFlights || hasSelectedNonDirectOption;
  }

  public getCodePlace(sorter: string): string {
    const { arrival, departure } = this.codePlaces;
    return this.orderCriteriaSectorNumber === 1 && !this.isLastSector ?
      sorter === SorterTypes.Arrival ? arrival : departure :
      sorter === SorterTypes.Arrival ? departure : arrival;
  }

  public checkboxTrigger(filter: AirFilterVm): void {
    this.checkboxEvent.emit({
      filter: filter,
      isMultiple: this.isMultipleSelection(filter),
      checked: this.isActiveCheckboxOption(filter.value as CheckboxFilterVm[])
    });
  }

  public rangeTrigger(filter: AirFilterVm, isMultiple: boolean): void {
    this.rangeEvent.emit({
      filter: filter,
      isMultiple: isMultiple,
      checked: this.isActiveRange(filter.type)
    });
  }

  public sortTrigger(filter: AirFilterVm): void {
    this.sortEvent.emit({
      filter: filter,
    });
  }

  public isActiveRange(type: string): boolean {
    return !isEmpty(this.getCheckedRanges(type));
  }

  public getCheckedRanges(type: string): string[] | number[] | RangePicketVm {
    if (!isEmpty(this.pickedRanges)) {
      switch (type) {
        case FilterTypes.PriceRange:
          return !isEmpty(this.pickedRanges[type]) ? this.pickedRanges[type] : null;
        case FilterTypes.DepartureTimes:
        case FilterTypes.SectorDurations:
          const _object = Object.assign({}, this.pickedRanges[type]);
          for (const p in _object) {
            if (isEmpty(_object[p])) {
              delete _object[p];
            }
          }
          return _object;
        default:
          return null;
      }
    } else {
      return null;
    }
  }

  private getCheckboxLabel(filter: AirFilterVm, range?: PickedRangeValues, key?: number): string {
    if (this.isMultipleSelection(filter)) {
      return this.translateService.translate(this.getLabelWithValuesToFilterBarTag(filter, range, key));
    } else if (filter.type === FilterTypes.Stops) {
      return this.translateService.translate('OFFERS_FLIGHT.DIRECT_FLIGHT');
    } else {
      return filter.value?.[0]?.Text;
    }
  }

  private getRangeLabel(filter: AirFilterVm, range?: PickedRangeValues, key?: number): string {
    return this.translateService.translate(this.getLabelWithValuesToFilterBarTag(filter, range, key));
  }

  private getLabelWithValuesToFilterBarTag(filter: AirFilterVm, range?: PickedRangeValues, key?: number): string {
    const isCheckboxFilter = this.isCheckboxFilter(filter);
    const isActiveCheckboxOption = isCheckboxFilter ? this.isActiveCheckboxOption(filter.value as CheckboxFilterVm[]) : false;
    return this.airFilterBarService.getLabelWithValuesToFilterBarTag(
      filter, this.pickedCheckboxes, isCheckboxFilter,
      isActiveCheckboxOption, this.isLastSector, this.codePlaces, { range, key }
    );
  }

  private getSortLabel(filter: AirFilterVm): string {
    if (
      (!this.isAirByPackage && this.orderCriteria) ||
      (this.isAirByPackage && this.sortFilterHasBeenChangedByPackage && this.orderCriteria)
    ) {
      return this.translateService.translate(this.getSorterTitle, { code: this.getCodePlace(this.orderCriteria) });
    } else {
      return this.translateService.translate(this.airFilterBarService.getItemLabel(filter.type));
    }
  }

}
